/**
 * MASHOW E-COMMERCE - IMAGE HELPERS
 * Tüm resim URL'lerini environment variable'dan oluşturur
 */

/**
 * Resim URL'ini oluşturur - ENV'den base URL alır
 * @param {string} imagePath - Resim yolu (/uploads/... veya tam URL)
 * @returns {string} Tam resim URL'i
 */
export const getImageUrl = (imagePath) => {
  // Boş ise placeholder SVG
  if (!imagePath) {
    return 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="300" height="200"%3E%3Crect fill="%23f3f4f6" width="300" height="200"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="sans-serif" font-size="16" fill="%239ca3af"%3EResim Yok%3C/text%3E%3C/svg%3E';
  }

  // Eğer tam URL ise ve yanlış domain'deyse düzelt
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    // www.mashow.com.tr'yi api.mashow.com.tr'ye çevir
    if (imagePath.includes('www.mashow.com.tr')) {
      return imagePath.replace('www.mashow.com.tr', 'api.mashow.com.tr');
    }
    // Zaten doğru domain'deyse direkt döndür
    return imagePath;
  }

  // VITE_API_URL'den base URL'i al
  const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
  let baseUrl = apiUrl;
  
  // Trim trailing slash first if any
  baseUrl = baseUrl.replace(/\/+$/, '');
  
  if (baseUrl.endsWith('/api')) {
    baseUrl = baseUrl.slice(0, -4);
  }
  
  // If baseUrl resolves to empty, default to window.location.origin
  if (!baseUrl) {
    baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
  }
  
  // Path'i temizle - /api/ veya api/ ile başlıyorsa çıkar
  let cleanPath = imagePath;
  if (cleanPath.startsWith('/api/')) {
    cleanPath = cleanPath.substring(4);
  } else if (cleanPath.startsWith('api/')) {
    cleanPath = '/' + cleanPath.substring(4);
  }
  
  // Başında / yoksa ekle
  if (!cleanPath.startsWith('/')) {
    cleanPath = `/${cleanPath}`;
  }
  
  // Birleştir ve döndür
  return `${baseUrl}${cleanPath}`;
};

/**
 * Resim objesi veya string için URL döndürür
 * @param {object|string} image - Resim objesi {url: '...'} veya direkt string
 * @returns {string} Tam resim URL'i
 */
export const getImageUrlFromObject = (image) => {
  if (!image) return getImageUrl(null);
  
  // Eğer obje ise url property'sini al
  if (typeof image === 'object' && image.url) {
    return getImageUrl(image.url);
  }
  
  // String ise direkt kullan
  if (typeof image === 'string') {
    return getImageUrl(image);
  }
  
  return getImageUrl(null);
};

/**
 * Dizideki resmin URL'sini al
 * @param {Array} images - Resim dizisi
 * @param {number} index - Hangi resmi almak istiyorsun (default: 0)
 * @returns {string} Tam resim URL'si
 */
export const getImageUrlFromArray = (images, index = 0) => {
  if (!images || !Array.isArray(images) || images.length === 0) {
    return getImageUrl(null);
  }
  
  const image = images[index];
  return getImageUrlFromObject(image);
};

/**
 * Ürün resimleri için helper
 * @param {Array} images - Resim array'i
 * @param {number} index - Hangi resim (default: 0)
 * @returns {string} Tam resim URL'i
 */
export const getProductImage = (images, index = 0) => {
  return getImageUrlFromArray(images, index);
};

/**
 * Kategori resmi için helper
 * @param {string|object} image - Kategori resmi
 * @returns {string} Tam resim URL'i
 */
export const getCategoryImage = (image) => {
  return getImageUrlFromObject(image);
};

/**
 * Site ayarları (logo, favicon, banner) için helper
 * @param {string} imagePath - Ayarlardan gelen resim yolu
 * @returns {string} Tam resim URL'i
 */
export const getSettingsImage = (imagePath) => {
  return getImageUrl(imagePath);
};

/**
 * Resim optimizasyonu için (gelecek için)
 * @param {string} url - Resim URL'i
 * @param {number} width - Genişlik
 * @param {number} height - Yükseklik
 * @returns {string} Optimize edilmiş resim URL'i
 */
export const getOptimizedImageUrl = (url, width, height) => {
  // Şimdilik sadece URL döndür
  // İleride Cloudinary veya başka bir servis ile entegre edilebilir
  return getImageUrl(url);
};
